import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getSummary } from '../services/api';
import { ThemeToggleButton, useTheme } from '../context/ThemeContext';
import Machines from './Machines.jsx';
import WorkOrders from './WorkOrders';
import Scrap from "./Scrap";
import Stock from './Stock';
import Maintenance from './Maintenance';
import Quality from './Quality';
import Energy from './Energy';
import Kaizen from './Kaizen';
import HR from './HR';
import Reports from './Reports';
import AdminPanel from './AdminPanel';
import Shifts from './Shifts';
import UserManagement from './UserManagement';
import SalesOrders from './SalesOrders';
import Purchasing from './Purchasing';
import {
    useLanguage,
    setLanguage,
    setTenantCurrencyState,
    syncAutomaticCurrencyForLanguage
} from '../lang/useLanguage';
import Products from './Products';
import NCR from "./NCR";
import QualityCost from "./QualityCost";
import Assembly from './Assembly';
import Organization from './Organization';
import EdgeManager from './EdgeManager';
import MainKpiGrid from "../components/dashboard/MainKpiGrid";
import RuntimeKpiGrid from "../components/dashboard/RuntimeKpiGrid";

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;

const PAGES_WITH_COMPONENT = [
    'machines', 'workorders', 'stock', 'maintenance', 'quality', 'energy',
    'kaizen', 'lean', 'fives', 'hr', 'organization', 'reports', 'admin', 'shifts', 'users',
    'salesorders', 'purchasing', 'products', 'scrap', 'ncr', 'qualitycost', 'edgemanager'
];

const ROLE_FALLBACK_PAGES = {
    purchasing: ['dashboard','stock','purchasing'],
    warehouse: ['dashboard','stock','assembly'],
    quality: ['dashboard','quality','ncr','qualitycost'],
    production: ['dashboard','workorders','assembly','quality','stock'],
    operator: ['dashboard','workorders','assembly','quality'],
    sales: ['dashboard','salesorders'],
    ic_satis_temsilci: ['dashboard','salesorders'],
    dis_ticaret_temsilci: ['dashboard','salesorders'],
};

export default function Dashboard() {
    const { page } = useParams();
    const active = page || 'dashboard';
    const navigate = useNavigate();

    // P6D.6 — permission route enforcement
    const { t } = useTheme();
    const mainRef = useRef(null);
    const { t: lang, lang: currentLang } = useLanguage();

    const handleLanguageChange = async () => {
        const nextLang = currentLang === 'tr' ? 'en' : 'tr';

        try {
            const token = localStorage.getItem("token");

            const res = await fetch(`${API}/tenant/settings`, {
                method: "PATCH",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    default_language: nextLang
                })
            });

            if (res.ok) {
                const data = await res.json();

                setTenantCurrencyState({
                    currency_code: data.currency_code,
                    currency_explicit: data.currency_explicit
                });

                if (!data.currency_explicit) {
                    syncAutomaticCurrencyForLanguage(
                        data.default_language || nextLang
                    );
                }

                setLanguage(data.default_language || nextLang);
                return;
            }

            /*
             * Tenant ayarı değiştirme yetkisi olmayan roller
             * kişisel/local dil değişimini kullanmaya devam eder.
             */
            if (res.status === 403) {
                syncAutomaticCurrencyForLanguage(nextLang);
                setLanguage(nextLang);
                return;
            }

            console.error(
                "Tenant language update failed:",
                res.status,
                await res.text()
            );
        } catch (e) {
            console.error("Tenant language update failed:", e);
        }
    };

    const MENU = [
        { id: 'dashboard', icon: '📊', label: lang.menu.dashboard },
        { id: 'machines', icon: '🏭', label: lang.menu.machines },
        { id: 'edgemanager', icon: '🧠', label: 'Edge Manager' },
        { id: 'workorders', icon: '📋', label: lang.menu.workorders },
        { id: 'scrap', icon: '♻️', label: lang.menu.scrap },
        { id: 'quality', icon: '✅', label: lang.menu.quality },
        { id: 'assembly', icon: '🔧', label: lang.menu.assembly },
        { id: 'stock', icon: '📦', label: lang.menu.stock },
        { id: 'maintenance', icon: '🔧', label: lang.menu.maintenance },
        { id: 'energy', icon: '⚡', label: lang.menu.energy },
        { id: 'kaizen', icon: '🔄', label: lang.menu.kaizen },
        { id: 'lean', icon: '🎯', label: lang.menu.lean },
        { id: 'fives', icon: '5️⃣', label: lang.menu.fives },
        { id: 'hr', icon: '👥', label: lang.menu.hr },
        { id: 'organization', icon: '🏢', label: lang.menu.organization },
        { id: 'reports', icon: '📈', label: lang.menu.reports },
        { id: 'shifts', icon: '📅', label: lang.menu.shifts },
        { id: 'admin', icon: '🎛️', label: lang.menu.admin },
        { id: 'users', icon: '🔐', label: lang.menu.users },
        { id: 'salesorders', icon: '🛒', label: lang.menu.salesorders },
        { id: 'purchasing', icon: '🛒', label: lang.menu.purchasing },
        { id: 'products', icon: '📦', label: lang.menu.products },
        { id: 'ncr', icon: '🔴', label: 'NCR & CAPA' },
        { id: 'qualitycost', icon: '💸', label: lang.menu.qualityCost },
    ];

    const [summary, setSummary] = useState(null);
    const [overview, setOverview] = useState(null);
    const [, setOverviewError] = useState("");
    const [machines, setMachines] = useState([]);
    const [alarms, setAlarms] = useState([]);
    const [, setWidgets] = useState({});
    const [permV2, setPermV2] = useState(null);
    const [planFeatures, setPlanFeatures] = useState(null);
    const [salesDash, setSalesDash] = useState(null);
    const [contentPerms, setContentPerms] = useState({}); // modül bazlı içerik izinleri
    const username = localStorage.getItem('username') || 'Admin';
    const [userProfile, setUserProfile] = useState(null);
    const userRole = localStorage.getItem('role') || 'operator';

    const isFullAccessRole = ['admin','manager','general_manager'].includes(userRole);
    const safeFallbackPages = useMemo(
        () => isFullAccessRole
            ? null
            : (ROLE_FALLBACK_PAGES[userRole] || ['dashboard']),
        [isFullAccessRole, userRole]
    );
    const [allowedPages, setAllowedPages] = useState(safeFallbackPages);


    useEffect(() => {
        // Full access roles use null = unrestricted.
        if (allowedPages === null) return;

        // Wait until we have a real permission array.
        if (!Array.isArray(allowedPages)) return;

        // Current page is allowed.
        if (allowedPages.includes(active)) return;

        // User is on an unauthorized route:
        // send them to the first allowed page that the Dashboard router can render.
        const firstAllowedPage = allowedPages.find(
            item =>
                item === 'dashboard' ||
                PAGES_WITH_COMPONENT.includes(item)
        );

        if (firstAllowedPage) {
            navigate(
                `/dashboard/${firstAllowedPage}`,
                { replace: true }
            );
        }
    }, [active, allowedPages, navigate]);


    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showNotifications, setShowNotifications] = useState(false);
    const notifRef = useRef(null);
    const [, setFinancialLoaded] = useState(false);
    const [plData, setPlData] = useState({
        toplam_satis: 0, brut_kar: 0, toplam_faaliyet_gideri: 0, net_kar: 0,
        toplam_maliyet: 0, toplam_fire_kaybi: 0, toplam_hurda_geliri: 0,
        toplam_iade_gelir_kaybi: 0, toplam_std_maliyet: 0, toplam_fiili_maliyet: 0,
        toplam_varyans: 0, toplam_iscilik_gideri: 0, bordro_personel_sayisi: 0,
        bordro_normal: 0, bordro_fazla_mesai: 0, toplam_enerji_gideri: 0,
        toplam_bakim_gideri: 0, bakim_iscilik: 0, bakim_parca: 0,
        enerji_detay: [], varyans_detay: []
        });

    // DB'den modül izinlerini + içerik izinlerini çek
    useEffect(() => {
        const fetchPermissions = async () => {
            try {
                const token = localStorage.getItem('token');
                const hdrs = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
                const [r1, r2, r3, r4, r5] = await Promise.all([
                    fetch(`${API}/users/permissions`, { headers: hdrs }),
                    fetch(`${API}/content-permissions`, { headers: hdrs }),
                    fetch(`${API}/permissions/v2/me`, { headers: hdrs }),
                    fetch(`${API}/users/me/profile`, { headers: hdrs }),
                    fetch(`${API}/tenant/plan-features`, { headers: hdrs }),
                ]);
                if (r1.ok) {
                    const d = await r1.json();
                    const perms = Array.isArray(d.permissions) ? d.permissions : [];

                    if (perms.includes('*')) {
                        setAllowedPages(null);
                    } else {
                        // P6D.4:
                        // Başarılı API cevabında kayıtlı rol izinları esastır.
                        // safeFallbackPages yalnız API hatasında kullanılır.
                        setAllowedPages([...new Set(perms)]);
                    }
                }
                if (r2.ok) {
                    const cp = await r2.json();
                    setContentPerms(cp);
                    // Dashboard widget izinlerini de buradan al
                    if (cp.dashboard) setWidgets(cp.dashboard);
                }

                if (r3.ok) {
                    const p = await r3.json();
                    setPermV2(p);
                }

                if (r4.ok) {
                    const up = await r4.json();
                    setUserProfile(up);
                }

                if (r5.ok) {
                    const pf = await r5.json();
                    setPlanFeatures(pf);
                }
            } catch {
                setAllowedPages(safeFallbackPages);
            }
        };
        fetchPermissions();
    }, [safeFallbackPages]);


    // Dashboard Live UX RC1
    const [dashboardRefreshing, setDashboardRefreshing] = useState(false);
    const [lastDashboardRefresh, setLastDashboardRefresh] = useState(null);
    const [dashboardRefreshTick, setDashboardRefreshTick] = useState(0);

    // Dashboard RC1.6A
    const dashboardRefreshRunningRef = useRef(false);

    const [productionPeriod, setProductionPeriod] = useState(() =>
    localStorage.getItem("dashboard_production_period") || "30d"
);

const handleProductionPeriodChange = (value) => {
    setProductionPeriod(value);
    localStorage.setItem(
        "dashboard_production_period",
        value
    );
};

// Dashboard Live UX RC1
// Sadece "kaç saniye önce" metnini günceller.
// Yeni API isteği oluşturmaz.
useEffect(() => {
    if (active !== "dashboard") return undefined;

    const tickInterval = window.setInterval(() => {
        setDashboardRefreshTick(v => v + 1);
    }, 1000);

    return () => {
        window.clearInterval(tickInterval);
    };
}, [active]);

const planModules = planFeatures?.modules || null;
    const planAllowsAll = Array.isArray(planModules) && planModules.includes('*');

    const systemAdminModules = ['admin', 'users', 'organization', 'edgemanager'];

    
const filteredMenu = MENU.filter(m => {

        // Edge Manager müşteri panelinde izleme ekranıdır.
        // Admin/manager/general_manager için rol ve plan filtresine takılmadan görünür.
        if (m.id === 'edgemanager' && isFullAccessRole) return true;

        const roleAllowed = (isFullAccessRole && allowedPages === null)
            ? true
            : (allowedPages || safeFallbackPages || ['dashboard']).includes(m.id);

        // Fabrika Paneli, Kullanıcı Yönetimi ve Organizasyon ticari paket modülü değil;
        // admin/manager/general_manager için plan filtresine takılmamalı.
        const bypassPlanFilter = isFullAccessRole && systemAdminModules.includes(m.id);

        const planAllowed = bypassPlanFilter
            ? true
            : planAllowsAll
                ? true
                : Array.isArray(planModules)
                    ? planModules.includes(m.id)
                    : true;

        return roleAllowed && planAllowed;
    });

useEffect(() => {
        const checkStock = async () => {
            try {
                await fetch(`${API}/stock/auto-check`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" }
                });
            } catch { }
        };
        const interval = setInterval(checkStock, 3600000); // Her 1 saat
        return () => clearInterval(interval);
    }, []);
    

    // Sayfa değişince en üste git
    useEffect(() => {
        if (mainRef.current) {
            mainRef.current.scrollTop = 0;
        }
    }, [active]);

    const createAuthHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json"
    });

    const applyOverviewResponse = async (overviewRes) => {
        if (overviewRes.ok) {
            const overviewData = await overviewRes.json();

            const machineStatuses = Array.isArray(
                overviewData?.machine_statuses
            )
                ? overviewData.machine_statuses
                : [];

            const activeAlarmRows = machineStatuses
                .filter(machine => {
                    const status = String(
                        machine?.status || ""
                    ).toLowerCase();

                    const alarmCode = String(
                        machine?.alarm_code ?? ""
                    ).trim();

                    return (
                        status === "fault" ||
                        status === "alarm" ||
                        status === "error" ||
                        (
                            alarmCode !== "" &&
                            alarmCode !== "0" &&
                            alarmCode.toLowerCase() !== "null"
                        )
                    );
                })
                .map(machine => ({
                    ...machine,

                    id:
                        machine?.id ??
                        machine?.machine_id ??
                        machine?.machine_code,

                    machine_id:
                        machine?.machine_id ??
                        machine?.machine_code ??
                        "",

                    machine_name:
                        machine?.machine_name ??
                        machine?.name ??
                        machine?.machine_id ??
                        "Makine",

                    code:
                        machine?.alarm_code ??
                        "ALARM",

                    message:
                        machine?.alarm_message ??
                        (
                            ["fault", "alarm", "error"].includes(
                                String(
                                    machine?.status || ""
                                ).toLowerCase()
                            )
                                ? "Makine arıza durumunda"
                                : "Aktif makine alarmı"
                        ),

                    alarm_code:
                        machine?.alarm_code ??
                        "ALARM",

                    alarm_message:
                        machine?.alarm_message ??
                        (
                            ["fault", "alarm", "error"].includes(
                                String(
                                    machine?.status || ""
                                ).toLowerCase()
                            )
                                ? "Makine arıza durumunda"
                                : "Aktif makine alarmı"
                        )
                }));

            setOverview(overviewData);
            setMachines(machineStatuses);
            setAlarms(activeAlarmRows);
            setOverviewError("");

            console.log(
                "Dashboard realtime overview yüklendi:",
                overviewData
            );

            return;
        }

        let detail = "";

        try {
            const errorData = await overviewRes.json();

            detail =
                errorData?.detail ||
                errorData?.message ||
                JSON.stringify(errorData);
        } catch {
            detail = await overviewRes.text();
        }

        const message =
            `Dashboard overview HTTP ${overviewRes.status}` +
            (detail ? `: ${detail}` : "");

        setOverviewError(message);
        console.error(message);
    };

    const loadRealtimeData = useCallback(async () => {
            try {
            const overviewRes = await fetch(
                `${API}/dashboard/overview?period=${encodeURIComponent(productionPeriod)}`,
                {
                    headers: createAuthHeaders()
                }
            );

            await applyOverviewResponse(overviewRes);

        } catch (error) {
            const message =
                error?.message ||
                "Dashboard canlı verileri yüklenemedi.";

            setOverviewError(message);

            console.error(
                "Dashboard realtime load error:",
                error
            );
        }
    }, [productionPeriod]);

    const loadBusinessData = useCallback(async () => {
        try {
            const authHeaders = createAuthHeaders();

            const [summaryResult, plResult, salesResult] =
                await Promise.allSettled([
                    getSummary(),

                    fetch(
                        `${API}/dashboard/pl-summary`,
                        {
                            headers: authHeaders
                        }
                    ),

                    fetch(
                        `${API}/dashboard/sales-v2`,
                        {
                            headers: authHeaders
                        }
                    )
                ]);

            // -------------------------------------------------
            // Genel özet
            // -------------------------------------------------
            if (
                summaryResult.status === "fulfilled" &&
                summaryResult.value?.data
            ) {
                setSummary(summaryResult.value.data);
            } else if (summaryResult.status === "rejected") {
                console.error(
                    "Dashboard summary load error:",
                    summaryResult.reason
                );
            }

            // -------------------------------------------------
            // Kar / zarar özeti
            // -------------------------------------------------
            if (
                plResult.status === "fulfilled" &&
                plResult.value?.ok
            ) {
                const d = await plResult.value.json();

                setPlData({
                    toplam_satis:
                        d.toplam_satis || 0,

                    brut_kar:
                        d.brut_kar || 0,

                    toplam_faaliyet_gideri:
                        d.toplam_faaliyet_gideri || 0,

                    net_kar:
                        d.net_kar || 0,

                    toplam_maliyet:
                        d.toplam_maliyet || 0,

                    toplam_fire_kaybi:
                        d.toplam_fire_kaybi || 0,

                    toplam_hurda_geliri:
                        d.toplam_hurda_geliri || 0,

                    toplam_iade_gelir_kaybi:
                        d.toplam_iade_gelir_kaybi || 0,

                    toplam_std_maliyet:
                        d.toplam_std_maliyet || 0,

                    toplam_fiili_maliyet:
                        d.toplam_fiili_maliyet || 0,

                    toplam_varyans:
                        d.toplam_varyans || 0,

                    toplam_iscilik_gideri:
                        d.toplam_iscilik_gideri || 0,

                    bordro_personel_sayisi:
                        d.bordro_personel_sayisi || 0,

                    bordro_normal:
                        d.bordro_normal || 0,

                    bordro_fazla_mesai:
                        d.bordro_fazla_mesai || 0,

                    toplam_enerji_gideri:
                        d.toplam_enerji_gideri || 0,

                    toplam_bakim_gideri:
                        d.toplam_bakim_gideri || 0,

                    bakim_iscilik:
                        d.bakim_iscilik || 0,

                    bakim_parca:
                        d.bakim_parca || 0,

                    enerji_detay:
                        d.enerji_detay || [],

                    varyans_detay:
                        d.varyans_detay || []
                });
            } else if (
                plResult.status === "fulfilled" &&
                !plResult.value?.ok
            ) {
                console.error(
                    "Dashboard P/L HTTP:",
                    plResult.value?.status
                );
            } else if (plResult.status === "rejected") {
                console.error(
                    "Dashboard P/L load error:",
                    plResult.reason
                );
            }

            // P/L isteğinin ilk denemesi tamamlandı.
            // Bundan sonraki 30 sn refresh'lerde skeleton geri gelmez.
            setFinancialLoaded(true);

            // -------------------------------------------------
            // Satış Dashboard verisi
            // -------------------------------------------------
            if (
                salesResult.status === "fulfilled" &&
                salesResult.value?.ok
            ) {
                const salesData =
                    await salesResult.value.json();

                setSalesDash(salesData);
            } else if (
                salesResult.status === "fulfilled" &&
                !salesResult.value?.ok
            ) {
                console.error(
                    "Dashboard sales HTTP:",
                    salesResult.value?.status
                );
            } else if (salesResult.status === "rejected") {
                console.error(
                    "Dashboard sales load error:",
                    salesResult.reason
                );
            }

            console.log(
                "Dashboard business verileri yenilendi."
            );
        } catch (error) {
            console.error(
                "Dashboard business load error:",
                error
            );
        }
    }, []);

useEffect(() => {
    let cancelled = false;

    const runAutoRefresh = async () => {
        if (cancelled) return;
        if (dashboardRefreshRunningRef.current) return;

        dashboardRefreshRunningRef.current = true;

        try {
            await Promise.allSettled([
                loadRealtimeData(),
                loadBusinessData()
            ]);

            if (!cancelled) {
                setLastDashboardRefresh(new Date());
            }
        } finally {
            dashboardRefreshRunningRef.current = false;
        }
    };

    runAutoRefresh();

    const dashboardInterval = window.setInterval(() => {
        if (document.hidden) return;
        runAutoRefresh();
    }, 30000);

    const handleVisibilityChange = () => {
        if (!document.hidden) {
            runAutoRefresh();
        }
    };

    document.addEventListener(
        "visibilitychange",
        handleVisibilityChange
    );

    return () => {
        cancelled = true;

        window.clearInterval(dashboardInterval);

        document.removeEventListener(
            "visibilitychange",
            handleVisibilityChange
        );
    };
}, [loadRealtimeData, loadBusinessData]);

const handleManualDashboardRefresh = async () => {
    if (
        dashboardRefreshing ||
        dashboardRefreshRunningRef.current
    ) {
        return;
    }

    dashboardRefreshRunningRef.current = true;
    setDashboardRefreshing(true);

    try {
        await Promise.allSettled([
            loadRealtimeData(),
            loadBusinessData()
        ]);

        setLastDashboardRefresh(new Date());
    } finally {
        dashboardRefreshRunningRef.current = false;
        setDashboardRefreshing(false);
    }
};

const fetchNotifications = useCallback(async () => {
        try {
            const res = await fetch(`${window.location.protocol}//${window.location.hostname}/api/notifications`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
            });
            if (res.ok) {
                const data = await res.json();
                setNotifications(data);
                setUnreadCount(data.filter(n => !n.is_read).length);
            }
        } catch { }
    }, []);

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    useEffect(() => {
        const handleClick = (e) => {
            if (notifRef.current && !notifRef.current.contains(e.target)) {
                setShowNotifications(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    const markAsRead = async (id) => {
        await fetch(`${window.location.protocol}//${window.location.hostname}/api/notifications/${id}/read`, {
            method: "PATCH",
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        });
        fetchNotifications();
    };

    const markAllRead = async () => {
        await fetch(`${window.location.protocol}//${window.location.hostname}/api/notifications/read-all`, {
            method: "PATCH",
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        });
        fetchNotifications();
    };

    const SALES_DASHBOARD_ROLES = new Set([
        "ic_satis_mudur",
        "ic_satis_uzman",
        "ic_satis_temsilci",
        "dis_ticaret_mudur",
        "dis_ticaret_uzman",
        "dis_ticaret_temsilci",
        "sales",
        "sales_manager"
    ]);

    const hasSalesDashboardV2 = !!(
        permV2?.allowed &&
        Object.keys(permV2.allowed).some(
            k => k.startsWith("dashboard:widget:sales_")
        )
    );

    const isSalesDashboardV2 =
        SALES_DASHBOARD_ROLES.has(userRole) ||
        hasSalesDashboardV2;

    const canSee = (key) => {
        const dashPerms = contentPerms?.dashboard;

        if (isSalesDashboardV2) {
            const v2Key = `dashboard:widget:${key}`;

            if (
                hasSalesDashboardV2 &&
                Object.prototype.hasOwnProperty.call(
                    permV2?.allowed || {},
                    v2Key
                )
            ) {
                return permV2.allowed[v2Key] === true;
            }

            if (!dashPerms || Object.keys(dashPerms).length === 0) {
                return true;
            }

            return dashPerms[key] !== false;
        }

        if (!dashPerms || Object.keys(dashPerms).length === 0) return true;
        return dashPerms[key] !== false;
    };

    const logout = () => {
        const keys = ["view_stock", "tab_stock", "view_quality", "view_machines",
            "view_workorders", "view_maintenance", "view_products"];
        const saved = {};
        keys.forEach(k => { saved[k] = localStorage.getItem(k); });
        localStorage.clear();
        sessionStorage.clear();
        keys.forEach(k => { if (saved[k]) localStorage.setItem(k, saved[k]); });
        window.location.href = '/login';
    };

    const isImpersonated = !!localStorage.getItem("superadmin_token");

    const backToSuperAdmin = () => {
        const oldToken = localStorage.getItem("superadmin_token");

        if (!oldToken) return;

        localStorage.setItem("token", oldToken);
        localStorage.removeItem("superadmin_token");
        localStorage.removeItem("impersonated_tenant");
        localStorage.removeItem("impersonated_user");

        window.location.href = "https://admin.opement.com";
    };


    const setActive = (id) => {
        navigate(`/dashboard/${id}`);
    };

    const statusColor = (s) => {
        if (s === 'running') return { color: '#10b981', bg: 'rgba(16,185,129,0.1)' };
        if (s === 'fault') return { color: '#ef4444', bg: 'rgba(239,68,68,0.1)' };
        return { color: '#6b7280', bg: 'rgba(107,114,128,0.1)' };
    };

    const statusLabel = (s) => {
        const status = String(s || "").toLowerCase();

        if (status === 'running') return lang.dashboard.statusRunning;
        if (status === 'fault') return lang.dashboard.statusFault;
        if (status === 'stopped') return lang.dashboard.statusStopped;
        if (status === 'idle') return lang.dashboard.statusIdle;
        if (status === 'offline') return lang.dashboard.statusOffline;
        if (status === 'setup') return lang.dashboard.statusSetup;
        if (status === 'maintenance') return lang.dashboard.statusMaintenance;
        if (status === 'tool_change') return lang.dashboard.statusToolChange;
        if (status === 'unknown') return lang.dashboard.statusUnknown;

        return s || lang.dashboard.statusUnknown;
    };

    const S = {
        layout: {
            display: 'flex', minHeight: '100vh',
            background: t.bg.page,
            fontFamily: "'Inter', sans-serif",
        },
        sidebar: {
            width: '240px', minWidth: '240px',
            background: t.bg.modal,
            borderRight: `1px solid ${t.border.default}`,
            display: 'flex', flexDirection: 'column',
            position: 'fixed', top: 0, left: 0,
            height: '100vh', overflowY: 'auto', zIndex: 100,
        },
        logo: {
            padding: '24px 20px',
            borderBottom: `1px solid ${t.border.default}`,
        },
        logoTitle: {
            fontSize: '18px', fontWeight: '700',
            color: t.text.primary, letterSpacing: '-0.3px',
            lineHeight: '1.2',
        },
        logoSub: {
            fontSize: '11px', color: t.text.muted, marginTop: '2px',
        },
        nav: { padding: '12px 8px', flex: 1 },
        navSection: {
            fontSize: '10px', fontWeight: '600', color: t.text.muted,
            letterSpacing: '0.8px', textTransform: 'uppercase',
            padding: '8px 12px 4px', marginTop: '8px',
        },
        navItem: (isActive) => ({
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '9px 12px', borderRadius: '8px', cursor: 'pointer',
            color: isActive ? t.text.primary : t.text.muted,
            background: isActive ? t.accent.blueBg : 'transparent',
            borderLeft: isActive ? `2px solid ${t.accent.blue}` : '2px solid transparent',
            fontSize: '13px', fontWeight: isActive ? '600' : '400',
            transition: 'all 0.15s', marginBottom: '2px',
        }),
        navIcon: { fontSize: '15px', width: '20px', textAlign: 'center' },
        main: {
            marginLeft: '240px', flex: 1,
            display: 'flex', flexDirection: 'column',
            height: '100vh', overflow: 'auto',
        },
        topbar: {
            background: t.bg.modal,
            borderBottom: `1px solid ${t.border.default}`,
            padding: '0 24px', height: '56px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            position: 'sticky', top: 0, zIndex: 99,
            flexShrink: 0,
        },
        pageTitle: {
            fontSize: '15px',
            fontWeight: '700',
            color: t.text.primary,
            letterSpacing: '-0.15px',
            whiteSpace: 'nowrap',
        },
        userInfo: {
            display: 'flex', alignItems: 'center', gap: '12px',
        },
        avatar: {
            width: '32px', height: '32px', borderRadius: '8px',
            background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '13px', fontWeight: '700', color: '#fff',
        },
        content: { padding: '24px', flex: 1 },
        statsGrid: {
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '16px', marginBottom: '24px',
        },
        statCard: (color) => ({
            background: t.bg.card,
            border: `1px solid ${t.border.card}`,
            borderRadius: '12px', padding: '20px',
            borderTop: `3px solid ${color}`,
            boxShadow: t.shadow.card,
        }),
        statLabel: {
            fontSize: '12px', color: t.text.muted, fontWeight: '500',
            marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px',
        },
        statValue: (color) => ({
            fontSize: '28px', fontWeight: '700', color, letterSpacing: '-1px',
        }),
        statSub: { fontSize: '12px', color: t.text.placeholder, marginTop: '4px' },
        grid2: {
            display: 'grid', gridTemplateColumns: '1fr 1fr',
            gap: '16px', marginBottom: '24px',
        },
        card: {
            background: t.bg.card,
            border: `1px solid ${t.border.card}`,
            borderRadius: '12px', padding: '20px',
            boxShadow: t.shadow.card,
        },
        cardTitle: {
            fontSize: '13px', fontWeight: '600', color: t.text.primary,
            marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px',
        },
        table: { width: '100%', borderCollapse: 'collapse' },
        th: {
            fontSize: '11px', color: t.text.muted, fontWeight: '600',
            textTransform: 'uppercase', letterSpacing: '0.5px',
            padding: '8px 12px', textAlign: 'left',
            borderBottom: `1px solid ${t.border.default}`,
        },
        td: {
            fontSize: '13px', color: t.text.secondary,
            padding: '10px 12px',
            borderBottom: `1px solid ${t.border.subtle}`,
        },
        badge: (color, bg) => ({
            display: 'inline-block', padding: '2px 8px', borderRadius: '20px',
            fontSize: '11px', fontWeight: '600', color, background: bg,
        }),
        logoutBtn: {
            margin: '16px', padding: '10px',
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.2)',
            borderRadius: '8px', color: '#ef4444', cursor: 'pointer',
            fontSize: '13px', fontWeight: '500', textAlign: 'center',
        },
        emptyModule: {
            background: t.bg.card,
            border: `1px solid ${t.border.card}`,
            borderRadius: '12px', padding: '40px',
            textAlign: 'center', color: t.text.muted,
            boxShadow: t.shadow.card,
        },
    };

    return (
        <div style={S.layout}>
            {/* SIDEBAR */}
            <aside style={S.sidebar}>
                <div style={S.logo}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                            width: '36px', height: '36px',
                            background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                            borderRadius: '10px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '18px',
                        }}>🏭</div>
                        <div>
                            <div style={S.logoTitle}>Opement</div>
                            <div style={{ fontSize: '11px', fontWeight: '500', color: t.text.primary, opacity: 0.8 }}>Smart Factory System</div>
                            <div style={S.logoSub}>MES · ERP · MRP</div>
                        </div>
                    </div>
                </div>

                <nav style={S.nav}>
                    <div style={S.navSection}>{lang.dashboard.mainModules}</div>
                    {filteredMenu.map(item => (
                        <div
                            key={item.id}
                            style={S.navItem(active === item.id)}
                            onClick={() => setActive(item.id)}
                        >
                            <span style={S.navIcon}>{item.icon}</span>
                            <span>{item.label}</span>
                        </div>
                    ))}
                </nav>

                {isImpersonated && (
                    <div
                        style={{
                            margin: '16px 16px 0',
                            padding: '10px',
                            background: 'rgba(245,158,11,0.12)',
                            border: '1px solid rgba(245,158,11,0.35)',
                            borderRadius: '8px',
                            color: '#fbbf24',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '700',
                            textAlign: 'center',
                        }}
                        onClick={backToSuperAdmin}
                    >
                        ← {lang.dashboard.backToSuperAdmin}
                    </div>
                )}

                <div style={S.logoutBtn} onClick={logout}>
                    🚪 {lang.logout}
                </div>
            </aside>

            {/* MAIN */}
            <main
                ref={mainRef}
                className="opement-dashboard-main"
                style={S.main}
            >
                <style>{`
                    /*
                     * Dashboard Responsive Polish RC1
                     * Desktop-first responsive grid system.
                     */

                    .opement-main-kpi-grid,
                    .opement-runtime-kpi-grid,
                    .opement-machine-cards-grid {
                        grid-template-columns:
                            repeat(4, minmax(0, 1fr)) !important;
                    }

                    .opement-dashboard-grid2 {
                        grid-template-columns:
                            repeat(2, minmax(0, 1fr)) !important;
                    }

                    .opement-executive-grid {
                        grid-template-columns:
                            1.05fr 1.05fr 1fr !important;
                    }

                    .opement-dashboard-topbar {
                        min-width: 0;
                    }

                    .opement-dashboard-topbar > * {
                        min-width: 0;
                    }

                    /*
                     * Normal laptop / 1366–1599 px
                     */
                    @media (max-width: 1599px) {
                        /*
                         * Compact executive dashboard:
                         * 1366/1440 ekranlarda da ana KPI'lar yatay kalır.
                         */
                        .opement-main-kpi-grid {
                            grid-template-columns:
                                repeat(4, minmax(0, 1fr)) !important;
                        }

                        .opement-runtime-kpi-grid {
                            grid-template-columns:
                                repeat(9, minmax(0, 1fr)) !important;
                        }

                        .opement-machine-cards-grid {
                            grid-template-columns:
                                repeat(4, minmax(0, 1fr)) !important;
                        }

                        .opement-dashboard-main {
                            min-width: 0;
                        }
                    }

                    /*
                     * Tablet / dar dashboard
                     */
                    @media (max-width: 1099px) {
                        .opement-main-kpi-grid,
                        .opement-runtime-kpi-grid,
                        .opement-machine-cards-grid,
                        .opement-dashboard-grid2,
                        .opement-executive-grid {
                            grid-template-columns:
                                minmax(0, 1fr) !important;
                        }

                        .opement-dashboard-topbar {
                            height: auto !important;
                            min-height: 56px;
                            padding-top: 8px !important;
                            padding-bottom: 8px !important;
                            gap: 10px;
                            flex-wrap: wrap;
                        }

                        .opement-dashboard-topbar > div:last-child {
                            margin-left: auto;
                            flex-wrap: wrap;
                        }
                    }

                    /*
                     * Küçük ekran
                     */
                    @media (max-width: 767px) {
                        .opement-dashboard-main {
                            margin-left: 0 !important;
                        }

                        .opement-dashboard-topbar {
                            padding-left: 14px !important;
                            padding-right: 14px !important;
                        }

                        .opement-main-kpi-grid,
                        .opement-runtime-kpi-grid,
                        .opement-machine-cards-grid {
                            gap: 12px !important;
                        }
                    }
                `}</style>

                {/* TOPBAR */}
                <div
                    className="opement-dashboard-topbar"
                    style={S.topbar}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            minWidth: 0
                        }}
                    >
                        <div style={S.pageTitle}>
                            {MENU.find(m => m.id === active)?.icon} {MENU.find(m => m.id === active)?.label}
                        </div>

                        {active === "dashboard" && (() => {
                            const secondsSinceRefresh = lastDashboardRefresh
                                ? Math.max(
                                    0,
                                    Math.floor(
                                        (Date.now() - lastDashboardRefresh.getTime()) / 1000
                                    )
                                )
                                : null;

                            const refreshText = dashboardRefreshing
                                ? lang.dashboard.refreshing.toUpperCase()
                                : lang.dashboard.live;

                            let refreshSubText =
                                lang.dashboard.waitingForData;

                            if (lastDashboardRefresh) {
                                if (secondsSinceRefresh <= 2) {
                                    refreshSubText =
                                        lang.dashboard.updatedJustNow;
                                } else {
                                    refreshSubText =
                                        `${secondsSinceRefresh} ${lang.dashboard.secondsAgo}`;
                                }
                            }

                            // dashboardRefreshTick render tetikleyicisidir.
                            void dashboardRefreshTick;

                            return (
                                <div
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 8,
                                        minHeight: 30,
                                        padding: "4px 10px",
                                        borderRadius: 999,
                                        border: dashboardRefreshing
                                            ? "1px solid rgba(245,158,11,0.30)"
                                            : "1px solid rgba(34,197,94,0.30)",
                                        background: dashboardRefreshing
                                            ? "rgba(245,158,11,0.08)"
                                            : "rgba(34,197,94,0.08)",
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    <span
                                        style={{
                                            width: 8,
                                            height: 8,
                                            flexShrink: 0,
                                            borderRadius: "50%",
                                            background: dashboardRefreshing
                                                ? "#f59e0b"
                                                : "#22c55e",
                                            boxShadow: dashboardRefreshing
                                                ? "0 0 0 4px rgba(245,158,11,0.10)"
                                                : "0 0 0 4px rgba(34,197,94,0.10)"
                                        }}
                                    />

                                    <span
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1,
                                            lineHeight: 1.05
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize: 9,
                                                fontWeight: 800,
                                                letterSpacing: "0.08em",
                                                color: dashboardRefreshing
                                                    ? "#f59e0b"
                                                    : "#4ade80"
                                            }}
                                        >
                                            {refreshText}
                                        </span>

                                        <span
                                            style={{
                                                fontSize: 9,
                                                fontWeight: 500,
                                                letterSpacing: 0,
                                                color: t.text.muted
                                            }}
                                        >
                                            {refreshSubText}
                                        </span>
                                    </span>
                                </div>
                            );
                        })()}
                    </div>
                    <div style={S.userInfo}>
                        <div style={{ fontSize: '12px', color: t.text.muted }}>
                            {new Date().toLocaleDateString(
                                currentLang === "en" ? "en-US" : "tr-TR",
                                { weekday: "long", day: "numeric", month: "long" }
                            )}
                        </div>
                        {active === "dashboard" && (
                            <button
                                onClick={handleManualDashboardRefresh}
                                disabled={dashboardRefreshing}
                                title={lang.dashboard.refreshTitle}
                                style={{
                                    height: 32,
                                    padding: "0 11px",
                                    borderRadius: 8,
                                    border: `1px solid ${t.border.input}`,
                                    background: t.bg.toggle,
                                    color: t.text.secondary,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 6,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    cursor: dashboardRefreshing
                                        ? "default"
                                        : "pointer",
                                    opacity: dashboardRefreshing
                                        ? 0.6
                                        : 1
                                }}
                            >
                                <span style={{ fontSize: 14 }}>
                                    ↻
                                </span>

                                {dashboardRefreshing
                                    ? lang.dashboard.refreshing
                                    : lang.dashboard.refresh}
                            </button>
                        )}

                        <button
                            onClick={handleLanguageChange}
                            style={{
                                padding: "6px 12px", borderRadius: 8, cursor: "pointer",
                                border: `1px solid ${t.border.input}`, background: t.bg.toggle,
                                color: t.text.secondary, fontSize: 12, fontWeight: 600,
                            }}
                        >
                            {currentLang === 'tr' ? 'EN' : 'TR'}
                        </button>
                        {/* Bildirim Zili */}
                        <div ref={notifRef} style={{ position: "relative" }}>
                            <button onClick={() => setShowNotifications(!showNotifications)} style={{ position: "relative", padding: "6px 10px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 16 }}>
                                🔔
                                {unreadCount > 0 && (
                                    <span style={{ position: "absolute", top: -4, right: -4, background: "#ef4444", color: "#fff", borderRadius: "50%", width: 18, height: 18, fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        {unreadCount > 9 ? "9+" : unreadCount}
                                    </span>
                                )}
                            </button>
                            {showNotifications && (
                                <div style={{ position: "absolute", top: "110%", right: 0, zIndex: 500, width: 360, background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 16, boxShadow: t.shadow.modal, overflow: "hidden" }}>
                                    <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary }}>🔔 {lang.dashboard?.notifications || (currentLang === "en" ? "Notifications" : "Bildirimler")}</div>
                                        {unreadCount > 0 && <button onClick={markAllRead} style={{ fontSize: 11, color: t.accent.blue, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>{lang.dashboard.markAllRead}</button>}
                                    </div>
                                    <div style={{ maxHeight: 400, overflowY: "auto" }}>
                                        {notifications.length === 0 ? (
                                            <div style={{ padding: "30px 0", textAlign: "center", color: t.text.placeholder, fontSize: 13 }}>{lang.dashboard.noNotifications}</div>
                                        ) : (
                                            notifications.slice(0, 20).map(n => (
                                                <div key={n.id} onClick={() => markAsRead(n.id)} style={{ padding: "12px 18px", borderBottom: `1px solid ${t.border.subtle}`, cursor: "pointer", background: n.is_read ? "transparent" : `${t.accent.blue}10`, display: "flex", gap: 12, alignItems: "flex-start" }}
                                                    onMouseEnter={e => e.currentTarget.style.background = t.bg.rowAlt}
                                                    onMouseLeave={e => e.currentTarget.style.background = n.is_read ? "transparent" : `${t.accent.blue}10`}>
                                                    <div style={{ fontSize: 20, flexShrink: 0 }}>
                                                        {n.type === "purchase_order" ? "📦" : n.type === "quality" ? "✅" : n.type === "alarm" ? "🚨" : n.type === "maintenance" ? "🔧" : "🔔"}
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontSize: 13, fontWeight: n.is_read ? 500 : 700, color: t.text.primary }}>{n.title}</div>
                                                        <div style={{ fontSize: 12, color: t.text.muted, marginTop: 2 }}>{n.message}</div>
                                                        <div style={{ fontSize: 11, color: t.text.placeholder, marginTop: 4 }}>{new Date(n.created_at).toLocaleString(
                                                            currentLang === "tr"
                                                                ? "tr-TR"
                                                                : "en-US"
                                                        )}</div>
                                                    </div>
                                                    {!n.is_read && <div style={{ width: 8, height: 8, borderRadius: "50%", background: t.accent.blue, flexShrink: 0, marginTop: 4 }} />}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <ThemeToggleButton
                            lightLabel={lang.dashboard?.lightTheme || (currentLang === "en" ? "Light Theme" : "Açık Tema")}
                            darkLabel={lang.dashboard?.darkTheme || (currentLang === "en" ? "Dark Theme" : "Koyu Tema")}
                            lightTitle={lang.dashboard?.switchToLightTheme || (currentLang === "en" ? "Switch to light theme" : "Açık temaya geç")}
                            darkTitle={lang.dashboard?.switchToDarkTheme || (currentLang === "en" ? "Switch to dark theme" : "Koyu temaya geç")}
                        />
                        <div style={S.avatar}>
                            {(userProfile?.full_name || username).charAt(0).toUpperCase()}
                        </div>
                        <div style={{ lineHeight: 1.15 }}>
                            <div style={{ fontSize: '13px', color: t.text.primary, fontWeight: 700 }}>
                                {userProfile?.full_name || username}
                            </div>
                            <div style={{ fontSize: '11px', color: t.text.secondary, marginTop: 2 }}>
                                {userProfile?.title ||
                                    (userRole === "purchasing"
                                        ? (currentLang === "en" ? "Purchasing" : "Satın Alma")
                                        : userRole)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* CONTENT */}
                <div style={S.content}>

                    {/* DASHBOARD */}
                    {active === 'dashboard' && (
                        <>
                            {isSalesDashboardV2 && (
                                <>
                                    <div style={S.statsGrid}>
                                        {canSee('sales_month_amount') && (
                                            <div style={S.statCard('#22c55e')}>
                                                <div style={S.statLabel}>{lang.dashboard.salesMonthAmount}</div>
                                                <div style={S.statValue('#22c55e')}>
                                                    {Number(salesDash?.summary?.month_sales_amount || 0).toLocaleString(
                                                        currentLang === "tr"
                                                            ? "tr-TR"
                                                            : "en-US",
                                                        { minimumFractionDigits: 2 }
                                                    )} ₺
                                                </div>
                                                <div style={S.statSub}>{lang.dashboard.salesMonthAmountDesc}</div>
                                            </div>
                                        )}
                                        {canSee('sales_order_count') && (
                                            <div style={S.statCard('#3b82f6')}>
                                                <div style={S.statLabel}>{lang.dashboard.salesOrderCount}</div>
                                                <div style={S.statValue('#3b82f6')}>{salesDash?.summary?.month_order_count || 0}</div>
                                                <div style={S.statSub}>{lang.dashboard.salesLast30Days}</div>
                                            </div>
                                        )}
                                        {canSee('sales_pending_orders') && (
                                            <div style={S.statCard('#f59e0b')}>
                                                <div style={S.statLabel}>{lang.dashboard.salesPendingOrders}</div>
                                                <div style={S.statValue('#f59e0b')}>{salesDash?.summary?.pending_orders || 0}</div>
                                                <div style={S.statSub}>{lang.dashboard.salesPendingOrdersDesc}</div>
                                            </div>
                                        )}
                                        {canSee('sales_waiting_shipment') && (
                                            <div style={S.statCard('#8b5cf6')}>
                                                <div style={S.statLabel}>{lang.dashboard.salesWaitingShipment}</div>
                                                <div style={S.statValue('#8b5cf6')}>{salesDash?.summary?.waiting_shipment || 0}</div>
                                                <div style={S.statSub}>{lang.dashboard.salesWaitingShipmentDesc}</div>
                                            </div>
                                        )}
                                        {canSee('sales_completed_orders') && (
                                            <div style={S.statCard('#10b981')}>
                                                <div style={S.statLabel}>{lang.dashboard.salesCompleted}</div>
                                                <div style={S.statValue('#10b981')}>{salesDash?.summary?.completed_orders || 0}</div>
                                                <div style={S.statSub}>{lang.dashboard.salesCompletedDesc}</div>
                                            </div>
                                        )}
                                        {canSee('sales_invoice_pending') && (
                                            <div style={S.statCard('#ef4444')}>
                                                <div style={S.statLabel}>{lang.dashboard.salesInvoicePending}</div>
                                                <div style={S.statValue('#ef4444')}>{salesDash?.summary?.invoice_pending || 0}</div>
                                                <div style={S.statSub}>{lang.dashboard.salesInvoicePendingDesc}</div>
                                            </div>
                                        )}
                                        {canSee('sales_customer_count') && (
                                            <div style={S.statCard('#06b6d4')}>
                                                <div style={S.statLabel}>{lang.dashboard.salesCustomerCount}</div>
                                                <div style={S.statValue('#06b6d4')}>{salesDash?.summary?.customer_count || 0}</div>
                                                <div style={S.statSub}>{lang.dashboard.salesCustomerCountDesc}</div>
                                            </div>
                                        )}
                                        {canSee('sales_cancelled_orders') && (
                                            <div style={S.statCard('#64748b')}>
                                                <div style={S.statLabel}>{lang.dashboard.salesCancelled}</div>
                                                <div style={S.statValue('#64748b')}>{salesDash?.summary?.cancelled_orders || 0}</div>
                                                <div style={S.statSub}>{lang.dashboard.salesCancelledDesc}</div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="opement-dashboard-grid2" style={S.grid2}>
                                        {canSee('sales_recent_orders') && (
                                            <div style={S.card}>
                                                <div style={S.cardTitle}>🛒 {lang.dashboard.recentSalesOrders}</div>
                                                <table style={S.table}>
                                                    <thead>
                                                        <tr>
                                                            <th style={S.th}>{lang.dashboard.orderNo}</th>
                                                            <th style={S.th}>{lang.dashboard.customer}</th>
                                                            <th style={S.th}>{lang.dashboard.status}</th>
                                                            <th style={S.th}>{lang.dashboard.amount}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(salesDash?.recent_orders || []).map(o => (
                                                            <tr key={o.order_no}>
                                                                <td style={S.td}>{o.order_no}</td>
                                                                <td style={S.td}>{o.customer_name}</td>
                                                                <td style={S.td}>{o.status}</td>
                                                                <td style={S.td}>{Number(o.total_amount || 0).toLocaleString(
                                                                    currentLang === "tr"
                                                                        ? "tr-TR"
                                                                        : "en-US"
                                                                )} ₺</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                        {canSee('sales_top_products') && (
                                            <div style={S.card}>
                                                <div style={S.cardTitle}>🏆 {lang.dashboard.topSellingProducts}</div>
                                                <table style={S.table}>
                                                    <thead>
                                                        <tr>
                                                            <th style={S.th}>{lang.dashboard.product}</th>
                                                            <th style={S.th}>{lang.dashboard.quantity}</th>
                                                            <th style={S.th}>{lang.dashboard.amount}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(salesDash?.top_products || []).map(p => (
                                                            <tr key={p.product_name}>
                                                                <td style={S.td}>{p.product_name}</td>
                                                                <td style={S.td}>{Number(p.total_qty || 0).toLocaleString(
                                                                    currentLang === "tr"
                                                                        ? "tr-TR"
                                                                        : "en-US"
                                                                )}</td>
                                                                <td style={S.td}>{Number(p.total_amount || 0).toLocaleString(
                                                                    currentLang === "tr"
                                                                        ? "tr-TR"
                                                                        : "en-US"
                                                                )} ₺</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {userRole === "purchasing" && (
                                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                    <div style={S.card}>
                                        <div style={{
                                            fontSize: 20,
                                            fontWeight: 800,
                                            color: t.text.primary,
                                            marginBottom: 6
                                        }}>
                                            🛒 {currentLang === "en" ? "Purchasing Dashboard" : "Satın Alma Paneli"}
                                        </div>
                                        <div style={{
                                            fontSize: 12,
                                            color: t.text.muted
                                        }}>
                                            {currentLang === "en"
                                                ? "Manage material requirements, purchasing requests and stock availability."
                                                : "Malzeme ihtiyaçlarını, satın alma taleplerini ve stok durumunu yönetin."}
                                        </div>
                                    </div>

                                    <div style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
                                        gap: 16
                                    }}>
                                        <div
                                            onClick={() => navigate("/dashboard/purchasing")}
                                            style={{
                                                ...S.card,
                                                cursor: "pointer",
                                                minHeight: 130
                                            }}
                                        >
                                            <div style={{
                                                fontSize: 28,
                                                marginBottom: 12
                                            }}>🛒</div>

                                            <div style={{
                                                fontSize: 15,
                                                fontWeight: 800,
                                                color: t.text.primary,
                                                marginBottom: 6
                                            }}>
                                                {currentLang === "en"
                                                    ? "Purchasing Requests"
                                                    : "Satın Alma Talepleri"}
                                            </div>

                                            <div style={{
                                                fontSize: 11,
                                                color: t.text.muted,
                                                lineHeight: 1.5
                                            }}>
                                                {currentLang === "en"
                                                    ? "Review material requirements and manage purchasing operations."
                                                    : "Malzeme ihtiyaçlarını inceleyin ve satın alma işlemlerini yönetin."}
                                            </div>
                                        </div>

                                        <div
                                            onClick={() => navigate("/dashboard/stock")}
                                            style={{
                                                ...S.card,
                                                cursor: "pointer",
                                                minHeight: 130
                                            }}
                                        >
                                            <div style={{
                                                fontSize: 28,
                                                marginBottom: 12
                                            }}>📦</div>

                                            <div style={{
                                                fontSize: 15,
                                                fontWeight: 800,
                                                color: t.text.primary,
                                                marginBottom: 6
                                            }}>
                                                {currentLang === "en"
                                                    ? "Stock Management"
                                                    : "Stok Yönetimi"}
                                            </div>

                                            <div style={{
                                                fontSize: 11,
                                                color: t.text.muted,
                                                lineHeight: 1.5
                                            }}>
                                                {currentLang === "en"
                                                    ? "Check current material stock and availability."
                                                    : "Mevcut malzeme stoklarını ve kullanılabilir miktarları kontrol edin."}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {userRole !== "purchasing" && (
                                <>
                            <MainKpiGrid

                                                            overview={overview}

                                                            summary={summary}

                                                            lang={lang}

                                                            styles={S}

                                                            canSee={canSee}

                                                            productionPeriod={productionPeriod}

                                                            onProductionPeriodChange={handleProductionPeriodChange}

                                                            currentLang={currentLang}

                                                        />

                            <RuntimeKpiGrid

                                                            overview={overview}

                                                            lang={lang}

                                                            currentLang={currentLang}
                                                            canSee={canSee}

                                                        />



{/* RC6.1 — Yönetici karar alanı */}



                            {/* P6E.5 — dashboard content permission wiring */}
                            {/* EXECUTIVE DASHBOARD ROW */}
                            <div
                                className="opement-executive-grid"
                                style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "1.05fr 1.05fr 1fr",
                                    gap: 12,
                                    marginBottom: 12,
                                    alignItems: "stretch"
                                }}
                            >
                                {/* MAKİNE DURUMU */}
                                <div
                                    style={{
                                        ...S.card,
                                        display: canSee('widget_machines')
                                            ? undefined
                                            : "none",
                                        height: "100%",
                                        boxSizing: "border-box"
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            marginBottom: 14
                                        }}
                                    >
                                        <div style={S.cardTitle}>
                                            🏭 {lang.dashboard.machineStatus}
                                        </div>

                                        <button
                                            onClick={() =>
                                                navigate("/dashboard/machines")
                                            }
                                            style={{
                                                padding: "5px 9px",
                                                borderRadius: 7,
                                                border:
                                                    `1px solid ${t.border.input}`,
                                                background: t.bg.toggle,
                                                color: t.text.secondary,
                                                fontSize: 10,
                                                cursor: "pointer"
                                            }}
                                        >
                                            {lang.dashboard.viewAll}
                                        </button>
                                    </div>

                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns:
                                                "1.2fr .55fr 1.15fr .9fr",
                                            gap: 10,
                                            padding: "0 0 7px",
                                            marginBottom: 2,
                                            borderBottom:
                                                `1px solid ${t.border.subtle}`,
                                            fontSize: 8,
                                            fontWeight: 700,
                                            letterSpacing: "0.06em",
                                            color: t.text.muted,
                                            textTransform: "uppercase"
                                        }}
                                    >
                                        <span>
                                            {lang.dashboard.machine}
                                        </span>

                                        <span>OEE</span>

                                        <span>
                                            {lang.dashboard.program}
                                        </span>

                                        <span>
                                            {lang.dashboard.progress}
                                        </span>
                                    </div>

                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 0
                                        }}
                                    >
                                        {machines.slice(0, 4).map(
                                            (m, index) => {
                                                const status = String(
                                                    m?.status || "unknown"
                                                ).toLowerCase();

                                                const sc =
                                                    statusColor(status);

                                                const machineName =
                                                    m?.machine_name ||
                                                    m?.name ||
                                                    m?.machine_id ||
                                                    m?.machine_code ||
                                                    "Makine";

                                                const machineCode =
                                                    m?.machine_code ||
                                                    m?.machine_id ||
                                                    "—";

                                                const oee =
                                                    Number(
                                                        m?.oee || 0
                                                    ).toFixed(1);

                                                const program =
                                                    m?.program_name ||
                                                    m?.program_no ||
                                                    "—";

                                                const progress =
                                                    Math.min(
                                                        100,
                                                        Math.max(
                                                            0,
                                                            Number(
                                                                m?.progress_percent ||
                                                                m?.work_order_progress ||
                                                                m?.oee ||
                                                                0
                                                            )
                                                        )
                                                    );

                                                return (
                                                    <div
                                                        key={
                                                            m?.machine_id ||
                                                            m?.id ||
                                                            index
                                                        }
                                                        onMouseEnter={(event) => {
                                                            event.currentTarget.style.background =
                                                                t.bg.rowAlt;
                                                            event.currentTarget.style.transform =
                                                                "translateX(2px)";
                                                        }}
                                                        onMouseLeave={(event) => {
                                                            event.currentTarget.style.background =
                                                                "transparent";
                                                            event.currentTarget.style.transform =
                                                                "translateX(0)";
                                                        }}
                                                        style={{
                                                            display: "grid",
                                                            gridTemplateColumns:
                                                                "1.2fr .55fr 1.15fr .9fr",
                                                            gap: 10,
                                                            alignItems: "center",
                                                            padding: "10px 6px",
                                                            borderRadius: 7,
                                                            background: "transparent",
                                                            transition:
                                                                "background 160ms ease, transform 160ms ease",
                                                            borderBottom:
                                                                index < 3
                                                                    ? `1px solid ${t.border.subtle}`
                                                                    : "none"
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                minWidth: 0
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    display:
                                                                        "flex",
                                                                    alignItems:
                                                                        "center",
                                                                    gap: 7
                                                                }}
                                                            >
                                                                <span
                                                                    style={{
                                                                        width: 7,
                                                                        height: 7,
                                                                        borderRadius:
                                                                            "50%",
                                                                        background:
                                                                            sc
                                                                    }}
                                                                />

                                                                <strong
                                                                    style={{
                                                                        fontSize: 11,
                                                                        color:
                                                                            t.text.primary,
                                                                        whiteSpace:
                                                                            "nowrap",
                                                                        overflow:
                                                                            "hidden",
                                                                        textOverflow:
                                                                            "ellipsis"
                                                                    }}
                                                                >
                                                                    {machineName}
                                                                </strong>
                                                            </div>

                                                            <div
                                                                style={{
                                                                    marginLeft:
                                                                        14,
                                                                    marginTop:
                                                                        2,
                                                                    fontSize: 9,
                                                                    color:
                                                                        t.text.muted
                                                                }}
                                                            >
                                                                {machineCode}
                                                            </div>
                                                        </div>

                                                        <div
                                                            style={{
                                                                fontSize: 11,
                                                                fontWeight: 700,
                                                                color:
                                                                    t.text.primary
                                                            }}
                                                        >
                                                            {oee}%
                                                        </div>

                                                        <div
                                                            title={program}
                                                            style={{
                                                                minWidth: 0,
                                                                fontSize: 10,
                                                                fontWeight: 600,
                                                                color:
                                                                    t.text.secondary,
                                                                overflow:
                                                                    "hidden",
                                                                textOverflow:
                                                                    "ellipsis",
                                                                whiteSpace:
                                                                    "nowrap"
                                                            }}
                                                        >
                                                            {program}
                                                        </div>

                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                gap: 7
                                                            }}
                                                        >
                                                            <span
                                                                style={{
                                                                    fontSize: 10,
                                                                    fontWeight: 700,
                                                                    color:
                                                                        t.text.secondary,
                                                                    minWidth: 30
                                                                }}
                                                            >
                                                                {Math.round(
                                                                    progress
                                                                )}
                                                                %
                                                            </span>

                                                            <div
                                                                style={{
                                                                    height: 5,
                                                                    flex: 1,
                                                                    borderRadius:
                                                                        999,
                                                                    background:
                                                                        "rgba(51,65,85,.55)",
                                                                    overflow:
                                                                        "hidden"
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        width:
                                                                            `${progress}%`,
                                                                        height:
                                                                            "100%",
                                                                        borderRadius:
                                                                            999,
                                                                        background:
                                                                            status ===
                                                                            "fault"
                                                                                ? "#ef4444"
                                                                                : "#22c55e"
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        )}
                                    </div>
                                </div>

                                {/* FABRİKA BEYNİ */}
                                <div
                                    style={{
                                        ...S.card,
                                        display: canSee('widget_kaizen')
                                            ? "flex"
                                            : "none",
                                        height: "100%",
                                        boxSizing: "border-box",
                                        flexDirection: "column"
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: 14
                                        }}
                                    >
                                        <div style={S.cardTitle}>
                                            {lang.dashboard.factoryBrain}
                                        </div>

                                        
                                    </div>

                                    {(
                                        overview?.factory_brain?.actions ||
                                        overview?.factory_brain?.recommendations ||
                                        overview?.factory_brain?.suggestions ||
                                        []
                                    )
                                        .slice(0, 3)
                                        .map((item, index) => {
                                            const level =
                                                String(
                                                    item?.level ||
                                                    item?.priority ||
                                                    "info"
                                                ).toLowerCase();

                                            const color =
                                                level === "critical" ||
                                                level === "high"
                                                    ? "#ef4444"
                                                    : level === "warning" ||
                                                      level === "medium"
                                                        ? "#f59e0b"
                                                        : "#22c55e";

                                            return (
                                                <div
                                                    key={index}
                                                    style={{
                                                        display: "grid",
                                                        gridTemplateColumns:
                                                            "32px 1fr auto",
                                                        gap: 10,
                                                        alignItems: "center",
                                                        padding: "10px 0",
                                                        borderBottom:
                                                            index < 2
                                                                ? `1px solid ${t.border.subtle}`
                                                                : "none"
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: 30,
                                                            height: 30,
                                                            borderRadius: 8,
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                            background:
                                                                `${color}18`,
                                                            color,
                                                            fontSize: 15
                                                        }}
                                                    >
                                                        {level === "critical" ||
                                                        level === "high"
                                                            ? "!"
                                                            : level ===
                                                                "warning"
                                                              ? "⚡"
                                                              : "✓"}
                                                    </div>

                                                    <div
                                                        style={{
                                                            minWidth: 0
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                fontSize: 11,
                                                                fontWeight: 700,
                                                                color:
                                                                    t.text.primary
                                                            }}
                                                        >
                                                            {item?.title ||
                                                                item?.message ||
                                                                item?.text ||
                                                                "—"}
                                                        </div>

                                                        {(item?.description ||
                                                            item?.impact) && (
                                                            <div
                                                                style={{
                                                                    marginTop:
                                                                        3,
                                                                    fontSize: 9,
                                                                    color:
                                                                        t.text.muted,
                                                                    overflow:
                                                                        "hidden",
                                                                    whiteSpace:
                                                                        "nowrap",
                                                                    textOverflow:
                                                                        "ellipsis"
                                                                }}
                                                            >
                                                                {item?.description ||
                                                                    item?.impact}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <span
                                                        style={{
                                                            padding:
                                                                "3px 7px",
                                                            borderRadius: 999,
                                                            background:
                                                                `${color}18`,
                                                            color,
                                                            fontSize: 8,
                                                            fontWeight: 700
                                                        }}
                                                    >
                                                        {level === "critical" ||
                                                        level === "high"
                                                            ? lang.dashboard.severityHigh
                                                            : lang.dashboard.severityInfo}
                                                    </span>
                                                </div>
                                            );
                                        })}

                                    {!(
                                        overview?.factory_brain?.actions ||
                                        overview?.factory_brain?.recommendations ||
                                        overview?.factory_brain?.suggestions ||
                                        []
                                    ).length && (
                                        <div
                                            style={{
                                                padding: "20px 0",
                                                textAlign: "center",
                                                color: t.text.muted,
                                                fontSize: 11
                                            }}
                                        >
                                            {lang.dashboard.noActiveRecommendations}
                                        </div>
                                    )}

                                    <button
                                        onClick={() =>
                                            navigate("/dashboard/kaizen")
                                        }
                                        style={{
                                            width: "100%",
                                            marginTop: "auto",
                                            padding: "8px 10px",
                                            borderRadius: 8,
                                            border:
                                                `1px solid ${t.border.input}`,
                                            background: "transparent",
                                            color: t.text.secondary,
                                            fontSize: 10,
                                            fontWeight: 600,
                                            cursor: "pointer"
                                        }}
                                    >
                                        {lang.dashboard.viewAllAnalyses}
                                    </button>
                                </div>

                                {/* KAR & ZARAR */}
                                <div
                                    style={{
                                        ...S.card,
                                        display: canSee('widget_pl')
                                            ? undefined
                                            : "none",
                                        height: "100%",
                                        boxSizing: "border-box"
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: 14
                                        }}
                                    >
                                        <div style={S.cardTitle}>
                                            {lang.dashboard.profitLoss}
                                        </div>

                                        <span
                                            style={{
                                                fontSize: 9,
                                                color: t.text.muted
                                            }}
                                        >
                                            {lang.dashboard.last30Days}
                                        </span>
                                    </div>

                                    {[
                                        [
                                            lang.dashboard.totalRevenue,
                                            plData?.toplam_satis || 0,
                                            "#22c55e"
                                        ],
                                        [
                                            lang.dashboard.totalExpense,
                                            (
                                                Number(
                                                    plData?.toplam_maliyet ||
                                                    0
                                                ) +
                                                Number(
                                                    plData?.toplam_faaliyet_gideri ||
                                                    0
                                                )
                                            ),
                                            "#ef4444"
                                        ],
                                        [
                                            lang.dashboard.grossProfit,
                                            plData?.brut_kar || 0,
                                            "#3b82f6"
                                        ]
                                    ].map(([label, value, color]) => (
                                        <div
                                            key={label}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent:
                                                    "space-between",
                                                padding: "11px 0",
                                                borderBottom:
                                                    `1px solid ${t.border.subtle}`
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: 10,
                                                    color:
                                                        t.text.secondary
                                                }}
                                            >
                                                {label}
                                            </span>

                                            <strong
                                                style={{
                                                    fontSize: 12,
                                                    color
                                                }}
                                            >
                                                {Number(
                                                    value
                                                ).toLocaleString(
                                                    currentLang === "tr"
                                                        ? "tr-TR"
                                                        : "en-US"
                                                )}{" "}
                                                ₺
                                            </strong>
                                        </div>
                                    ))}

                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            paddingTop: 11
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize: 10,
                                                color: t.text.secondary
                                            }}
                                        >
                                            {lang.dashboard.grossMargin}
                                        </span>

                                        <strong
                                            style={{
                                                fontSize: 12,
                                                color: "#3b82f6"
                                            }}
                                        >
                                            %
                                            {Number(
                                                plData?.toplam_satis || 0
                                            ) > 0
                                                ? (
                                                    Number(
                                                        plData?.brut_kar ||
                                                        0
                                                    ) /
                                                    Number(
                                                        plData?.toplam_satis ||
                                                        1
                                                    ) *
                                                    100
                                                ).toFixed(1)
                                                : "0.0"}
                                        </strong>
                                    </div>
                                </div>
                            </div>

                            <div
                                className="opement-dashboard-alert-grid"
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "minmax(0, 1fr)",
                                    gap: 14,
                                    marginBottom: 24
                                }}
                            >
                                {/* Compact Machine Status yukarıdaki executive satırında gösteriliyor. */}
                                {canSee('widget_alarms') && (
                                    <div style={S.card}>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                marginBottom: 12
                                            }}
                                        >
                                            <div style={S.cardTitle}>
                                                🚨 {lang.dashboard.lastAlarms}
                                            </div>

                                            <button
                                                onClick={() =>
                                                    navigate("/dashboard/machines")
                                                }
                                                style={{
                                                    padding: "5px 9px",
                                                    borderRadius: 7,
                                                    border:
                                                        `1px solid ${t.border.input}`,
                                                    background: t.bg.toggle,
                                                    color: t.text.secondary,
                                                    fontSize: 10,
                                                    fontWeight: 600,
                                                    cursor: "pointer"
                                                }}
                                            >
                                                {lang.dashboard.viewAllArrow}
                                            </button>
                                        </div>

                                        {alarms.length === 0 ? (
                                            <div
                                                style={{
                                                    color: t.accent.green,
                                                    fontSize: 12,
                                                    padding: "24px 0",
                                                    textAlign: "center"
                                                }}
                                            >
                                                ✓ {lang.dashboard.noActiveAlarms}
                                            </div>
                                        ) : (
                                            <div
                                                style={{
                                                    width: "100%",
                                                    overflowX: "auto"
                                                }}
                                            >
                                                <table style={S.table}>
                                                    <thead>
                                                        <tr>
                                                            <th style={S.th}>
                                                                {lang.dashboard.time}
                                                            </th>

                                                            <th style={S.th}>
                                                                {lang.machine}
                                                            </th>

                                                            <th style={S.th}>
                                                                {lang.dashboard.alarmCode}
                                                            </th>

                                                            <th style={S.th}>
                                                                {lang.dashboard.description}
                                                            </th>

                                                            <th style={S.th}>
                                                                {lang.status}
                                                            </th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {alarms
                                                            .slice(0, 8)
                                                            .map((a, i) => {
                                                                const alarmStatus =
                                                                    String(
                                                                        a?.status ||
                                                                        "fault"
                                                                    ).toLowerCase();

                                                                const alarmSc =
                                                                    statusColor(
                                                                        alarmStatus
                                                                    );

                                                                const rawTime =
                                                                    a?.created_at ||
                                                                    a?.timestamp ||
                                                                    a?.started_at ||
                                                                    a?.occurred_at ||
                                                                    a?.updated_at ||
                                                                    a?.last_seen;

                                                                let alarmTime = "—";

                                                                if (rawTime) {
                                                                    const date =
                                                                        new Date(
                                                                            rawTime
                                                                        );

                                                                    if (
                                                                        !Number.isNaN(
                                                                            date.getTime()
                                                                        )
                                                                    ) {
                                                                        alarmTime =
                                                                            date.toLocaleString(
                                                                                currentLang === "tr"
                                                                                    ? "tr-TR"
                                                                                    : "en-US",
                                                                                {
                                                                                    day: "2-digit",
                                                                                    month: "2-digit",
                                                                                    hour: "2-digit",
                                                                                    minute: "2-digit"
                                                                                }
                                                                            );
                                                                    }
                                                                }

                                                                return (
                                                                    <tr
                                                                        key={
                                                                            a?.id ||
                                                                            `${a?.machine_id}-${a?.alarm_code}-${i}`
                                                                        }
                                                                        onMouseEnter={(event) => {
                                                                            event.currentTarget.style.background =
                                                                                t.bg.rowAlt;
                                                                        }}
                                                                        onMouseLeave={(event) => {
                                                                            event.currentTarget.style.background =
                                                                                "transparent";
                                                                        }}
                                                                        style={{
                                                                            transition:
                                                                                "background 160ms ease"
                                                                        }}
                                                                    >
                                                                        <td
                                                                            style={{
                                                                                ...S.td,
                                                                                color:
                                                                                    t.text.muted,
                                                                                fontSize: 10,
                                                                                whiteSpace:
                                                                                    "nowrap"
                                                                            }}
                                                                        >
                                                                            {alarmTime}
                                                                        </td>

                                                                        <td
                                                                            style={{
                                                                                ...S.td,
                                                                                color:
                                                                                    t.text.primary,
                                                                                fontWeight: 600
                                                                            }}
                                                                        >
                                                                            {a?.machine_name ||
                                                                                a?.machine_id ||
                                                                                lang.dashboard.machine}
                                                                        </td>

                                                                        <td
                                                                            style={{
                                                                                ...S.td,
                                                                                color:
                                                                                    t.accent.red,
                                                                                fontWeight: 700
                                                                            }}
                                                                        >
                                                                            {a?.alarm_code ||
                                                                                a?.code ||
                                                                                "ALARM"}
                                                                        </td>

                                                                        <td
                                                                            style={{
                                                                                ...S.td,
                                                                                color:
                                                                                    t.text.secondary
                                                                            }}
                                                                        >
                                                                            {a?.alarm_message ||
                                                                                a?.message ||
                                                                                lang.dashboard.activeMachineAlarm}
                                                                        </td>

                                                                        <td style={S.td}>
                                                                            <span
                                                                                style={S.badge(
                                                                                    alarmSc.color,
                                                                                    alarmSc.bg
                                                                                )}
                                                                            >
                                                                                {statusLabel(
                                                                                    alarmStatus
                                                                                )}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                            )}
                        </>
                    )}

                    {/* MODÜLLER */}

                    {/* MODÜLLER */}
                    {active === 'machines' && <Machines />}
                    {active === 'edgemanager' && <EdgeManager />}
                    {active === 'workorders' && <WorkOrders />}
                    {active === 'scrap' && <Scrap />}
                    {active === 'stock' && <Stock />}
                    {active === 'maintenance' && <Maintenance />}
                    {active === 'quality' && <Quality />}
                    {active === 'energy' && <Energy />}
                    {active === 'kaizen' && <Kaizen />}
                    {active === 'lean' && <Kaizen />}
                    {active === 'fives' && <Kaizen />}
                    {active === 'hr' && <HR />}
                    {active === 'organization' && <Organization />}
                    {active === 'reports' && <Reports />}
                    {active === 'admin' && <AdminPanel />}
                    {active === 'shifts' && <Shifts />}
                    {active === 'users' && <UserManagement />}
                    {active === 'salesorders' && <SalesOrders />}
                    {active === 'purchasing' && <Purchasing />}
                    {active === 'products' && <Products />}
                    {active === 'ncr' && <NCR />}
                    {active === 'qualitycost' && <QualityCost />}
                    {active === 'assembly' && <Assembly />}

                    {/* HENÜZ HAZIR OLMAYAN MODÜLLER */}
                    {active !== 'dashboard' && !PAGES_WITH_COMPONENT.includes(active) && (
                        <div style={S.emptyModule}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                                {MENU.find(m => m.id === active)?.icon}
                            </div>
                            <div style={{ fontSize: '16px', fontWeight: '600', color: t.text.secondary, marginBottom: '8px' }}>
                                {MENU.find(m => m.id === active)?.label} {lang.dashboard.moduleSuffix}
                            </div>
                            
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
